import React, { useEffect, useRef, useState } from 'react';
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { useDispatch, useSelector } from 'react-redux';
import { apiRunWithAxios } from './APIRunWithAxios';
import Loader from '../../../../components/ui/Loader/Loader';
import { toast } from 'react-toastify';
import swal from 'sweetalert';
import DateService from '../../../../services/DateService';
import { authLoginAndGetJWTToken } from '../../../../store/actions/OmniComm/OmniCommAuthAction';

function FuelValueDataGraph({ formData }) {
    const dispatch = useDispatch();
    const { data } = useSelector(state => state.omniAuthLoginReducer);
    const rootRef = useRef(null);
    
    const [loading, setLoading] = useState(false);
    const [chartData, setChartData] = useState([]);
    const [maxYAxis, setMaxYAxis] = useState(0)

    useEffect(() => {
        if (formData?.date_range_start && formData?.date_range_end) {
            getFuelValue();
        }
    }, [formData, data]);

    useEffect(() => {
        if (chartData.length > 0) {
            if (rootRef.current) {
                rootRef.current.dispose();
                rootRef.current = null;
            }
            
            const timer = setTimeout(() => {
                createChart();
            }, 100);
            
            return () => clearTimeout(timer);
        }
        
        return () => {
            if (rootRef.current) {
                rootRef.current.dispose();
                rootRef.current = null;
            }
        };
    }, [chartData]);

    const formatTimestamp = (timestamp) => {
        const date = new Date(timestamp * 1000);
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}:${String(date.getSeconds()).padStart(2, '0')}`;
    };

    const createChart = () => {
        const container = document.getElementById("fuelValueChart");
        if (!container) {
            return;
        }

        try {
            const root = am5.Root.new("fuelValueChart");
            rootRef.current = root;
            root.setThemes([am5themes_Animated.new(root)]);

            const chart = root.container.children.push(
                am5xy.XYChart.new(root, {
                    panX: true,
                    panY: true,
                    wheelX: "panX",
                    wheelY: "zoomX",
                    pinchZoomX: true
                })
            );

            // X Axis
            const xAxis = chart.xAxes.push(
                am5xy.DateAxis.new(root, {
                    baseInterval: { timeUnit: "minute", count: 30 },
                    renderer: am5xy.AxisRendererX.new(root, {
                        minGridDistance: 80,
                        minorGridEnabled: true
                    }),
                    tooltip: am5.Tooltip.new(root, {})
                })
            );

            // Format X-axis labels
            xAxis.get("renderer").labels.template.setAll({
                fontSize: 11,
                fill: am5.color(0x666666),
                rotation: -45,
                centerX: am5.p50,
                centerY: am5.p50,
                dy: 15
            });

            // Y Axis
            const yAxis = chart.yAxes.push(
                am5xy.ValueAxis.new(root, {
                    renderer: am5xy.AxisRendererY.new(root, {}),
                    title: am5.Label.new(root, { 
                        text: "Fuel Volume (Liters)",
                        fontSize: 12,
                        fontWeight: "bold"
                    }),
                    min: 0,
                    max: {maxYAxis},
                    strictMinMax: true
                })
            );

            // Add grid lines
            yAxis.get("renderer").grid.template.setAll({
                stroke: am5.color(0xe0e0e0),
                strokeWidth: 1,
                strokeDasharray: [5, 5]
            });

            xAxis.get("renderer").grid.template.setAll({
                stroke: am5.color(0xe0e0e0),
                strokeWidth: 1,
                strokeDasharray: [5, 5]
            });

            // Create series for Raw Volume (rV) - WITHOUT BULLETS
            const rVSeries = chart.series.push(
                am5xy.LineSeries.new(root, {
                    name: "Raw Volume (rV)",
                    xAxis: xAxis,
                    yAxis: yAxis,
                    valueYField: "rV",
                    valueXField: "date",
                    tooltip: am5.Tooltip.new(root, { 
                        labelText: "Raw Volume: {valueY} L"
                    }),
                    stroke: am5.color("#9C27B0"),
                    strokeWidth: 2
                })
                // labelText: "Raw Volume: {valueY} L\nDate: {date}"
            );

            // NO BULLETS - Commented out or removed
            // rVSeries.bullets.push(...) - REMOVED

            // Create series for Approx Volume (aV) - WITHOUT BULLETS
            const aVSeries = chart.series.push(
                am5xy.LineSeries.new(root, {
                    name: "Approx Volume (aV)",
                    xAxis: xAxis,
                    yAxis: yAxis,
                    valueYField: "aV",
                    valueXField: "date",
                    tooltip: am5.Tooltip.new(root, { 
                        labelText: "Approx Volume: {valueY} L"
                    }),
                    stroke: am5.color("#2196F3"),
                    strokeWidth: 3
                })
                // labelText: "Approx Volume: {valueY} L\nDate: {date}"
            );

            // NO BULLETS - Commented out or removed
            // aVSeries.bullets.push(...) - REMOVED

            // Set data
            rVSeries.data.setAll(chartData);
            aVSeries.data.setAll(chartData);

            // Add cursor
            chart.set("cursor", am5xy.XYCursor.new(root, {}));

            // Add scrollbar
            chart.set("scrollbarX", am5.Scrollbar.new(root, {
                orientation: "horizontal"
            }));

            // Add legend
            const legend = chart.children.push(
                am5.Legend.new(root, {
                    centerX: am5.p50,
                    x: am5.p50,
                    layout: root.horizontalLayout,
                    marginTop: 10,
                    marginBottom: 10
                })
            );
            legend.data.setAll([rVSeries, aVSeries]);

            // Animation
            chart.appear(1000, 100);            
        } catch (error) {
            console.error("Error creating chart:", error);
        }
    };

    const getFuelValue = async () => {
        if (data?.jwt) {
            setChartData([]);
            try {
                setLoading(true);
                let timeBegin, timeEnd;
                
                if (formData?.timeBegin && formData?.timeEnd) {
                    timeBegin = formData.timeBegin;
                    timeEnd = formData.timeEnd;
                } else if (formData?.date_range_start && formData?.date_range_end) {
                    timeBegin = Math.floor(new Date(formData.date_range_start).getTime() / 1000);
                    timeEnd = Math.floor(new Date(formData.date_range_end).getTime() / 1000);
                } else {
                    const now = Math.floor(Date.now() / 1000);
                    timeBegin = now - (3 * 24 * 60 * 60);
                    timeEnd = now;
                }
                
                const vehicleID = formData?.asset_id;
                if(vehicleID && timeBegin && timeEnd) {
                    let params = { 
                        timeBegin: timeBegin, 
                        timeEnd: timeEnd, 
                        vehicleID 
                    };
                    
                    let response = await apiRunWithAxios(
                        "https://online.omnicomm.in/ls/api/v1/reports/fuellevel", 
                        "GET", 
                        data?.jwt, 
                        params, 
                        {}
                    );
                    const results = response.data;
                    
                    if (results && results.tankData && results.tankData[0] && results.tankData[0].data) {
                        const transformedData = results.tankData[0].data
                            .filter(item => item.eD && (item.rV !== undefined || item.aV !== undefined))
                            .map(item => ({
                                date: item.eD * 1000,
                                rV: item.rV || 0,
                                aV: item.aV || 0,
                                formattedDate: formatTimestamp(item.eD)
                            }))
                            .sort((a, b) => a.date - b.date);
                        
                        const maxRV     =   Math.max(...transformedData.map(item => item.rV));
                        setMaxYAxis(maxRV)
                        setChartData(transformedData);
                    } 
                } else {
                    toast.error('Select asset & time period both are required, for statistic report.', { position: toast.POSITION.TOP_RIGHT });
                }
            } catch (error) {
                if(error?.response?.status === 401 && error?.response?.data?.error === "Unauthorized") {
                    swal({
                        title: "Refresh",
                        text: "Please refresh again for fetch fuel value data.",
                        icon: "warning",
                        // dangerMode: true,
                        buttons: ["Cancel", "Refresh"],
                    }).then(willDelete => {
                        if (willDelete) {
                            dispatch(authLoginAndGetJWTToken());
                        }
                    });
                }
            } finally {
                setLoading(false);
            }
        }
    };

    return (
        <div style={{ width: "100%", padding: "20px", background: "#fff", borderRadius: "10px" }}>
            <h3 style={{ marginBottom: "20px", color: "#333" }}>
                Fuel Volume Measurements
            </h3>
            <div style={{ marginBottom: "15px", color: "#666", fontSize: "14px" }}>
                <span>📊 Vehicle ID: {formData?.asset_id}</span>
                <span style={{ marginLeft: "20px" }}>
                    📅 Period: {DateService.dateFormat(formData?.date_range_start) || 'Last 3 days'} to {DateService.dateFormat(formData?.date_range_end) || 'Now'}
                </span>
            </div>
            {loading ? (
                <Loader />
            ) : chartData.length > 0 ? (
                <div 
                    id="fuelValueChart" 
                    style={{ 
                        width: "100%", 
                        height: "600px",
                        backgroundColor: "#fff"
                    }}
                ></div>
            ) : (
                <div style={{ 
                    textAlign: "center", 
                    padding: "100px 50px", 
                    color: "#666",
                    backgroundColor: "#f9f9f9",
                    borderRadius: "8px"
                }}>
                    <div style={{ fontSize: "48px", marginBottom: "20px" }}>📊</div>
                    <h4>No data available</h4>
                    <p>No fuel volume data found for the selected period and vehicle.</p>
                </div>
            )}
        </div>
    );
}

export default FuelValueDataGraph;